import type { Campaign } from "../types/contract";
import { campaignStatusMeta } from "./meta";

/**
 * Amazon キャンペーンの「配信状態」バッジ（2026-10-30）。
 * Meta/Google の status（ACTIVE/PAUSED だけ）では、SB の審査72h・広告ゼロ・予算切れが読めないため、
 * state ＋ 広告件数/審査状態 ＋ 当日消化率 ＋ 開始日から表示を決める。上から順に最初に当てはまったものを採用する。
 */
export interface AmazonDeliveryStatus {
  key: string;
  label: string;
  c: string;
  bg: string;
  /** バッジの下に出す1行（任意） */
  sub?: string;
}

/** 今日（JST）を YYYY-MM-DD で返す。テストでは now を差し替える。 */
export function todayJst(now: Date = new Date()): string {
  return new Date(now.getTime() + 9 * 3600 * 1000).toISOString().slice(0, 10);
}

export function amazonDeliveryStatus(c: Campaign, now: Date = new Date()): AmazonDeliveryStatus {
  const amz = c.amazon;
  const state = amz?.state;
  const ads = amz?.ads ?? null; // 古いキャッシュ応答には無い → null（不明扱い・バッジを出さない）
  const pct = amz?.budget_usage_pct ?? null;
  const paused = state === "PAUSED";

  if (state === "ARCHIVED" || c.status === "ARCHIVED") {
    return { key: "ARCHIVED", label: "終了", c: "var(--st-ended-c)", bg: "var(--st-ended-bg)" };
  }
  if (ads && ads.total === 0) {
    return {
      key: "NO_ADS",
      label: "広告なし",
      c: "var(--st-pending-c)",
      bg: "var(--st-pending-bg)",
      sub: paused ? "一時停止中 ・ 広告が未作成" : "広告が未作成",
    };
  }
  if (ads && ads.rejected > 0) {
    return { key: "REJECTED", label: "審査NG", c: "var(--danger)", bg: "var(--danger-bg)", sub: `${ads.rejected}本が却下` };
  }
  if (ads && ads.pending_review > 0) {
    return {
      key: "PENDING_REVIEW",
      label: "審査中",
      c: "var(--st-pending-c)",
      bg: "var(--st-pending-bg)",
      sub: paused ? "一時停止中 ・ 審査 最大72時間" : "審査 最大72時間",
    };
  }
  if (state === "ENABLED" && pct != null && pct >= 100) {
    return { key: "BUDGET_OUT", label: "予算切れ", c: "var(--danger)", bg: "var(--danger-bg)", sub: `当日消化 ${Math.round(pct)}%` };
  }
  if (paused) {
    return { key: "PAUSED", label: "一時停止", c: "var(--st-paused-c)", bg: "var(--st-paused-bg)" };
  }
  if (state === "ENABLED" && c.start && c.start > todayJst(now)) {
    return { key: "SCHEDULED", label: "開始待ち", c: "var(--st-paused-c)", bg: "var(--st-paused-bg)", sub: `${c.start} 開始` };
  }
  if (state === "ENABLED") {
    return {
      key: "ACTIVE",
      label: "配信中",
      c: "var(--st-active-c)",
      bg: "var(--st-active-bg)",
      sub: pct != null ? `当日消化 ${Math.round(pct)}%` : undefined,
    };
  }
  const m = campaignStatusMeta(c.status);
  return { key: "OTHER", label: m.label, c: m.c, bg: m.bg };
}
